import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filter, get, includes, isEmpty, min, uniq } from "lodash";
import {
  Avatar,
  Box,
  Card,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import Slider from "react-slick";
import { makeStyles } from "../../AppContainer/mui-theme";
import "../slick-theme.css";
import Image from "../../image";
import { mediaTypes } from "../../../utils/media";
import NoImageRenderer from "../../no-image-renderer";
import { navigateTo } from "../../../utils/location";
import {
  setFeaturedProductToByInOrder,
  startNewOrder,
} from "../../../store/order/actions";
import { openNewOrPrevOrderModal } from "../../../store/app/actions";
import { getFeaturedItems } from "../../../store/selectors";
import AllergensDecorator from "../../allergens-decorator";

const FeaturedWidget = (props) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const {
    appStyles,
    widget,
    pageContext,
    pageContext: {
      business: { allergens },
    },
  } = props;
  const featuredItems = useSelector((state) => getFeaturedItems(state, props));
  const order = useSelector((state) => state.order);

  const sliderRef = useRef(null);
  const dotsRef = useRef(null);

  const handleAddFeaturedItemToOrder = (item) => {
    const { orderItems } = order;

    const skipServingOptionsPage = get(
      appStyles,
      "skipServingOptionsPage",
      false
    );

    const isDuringOrder = !isEmpty(orderItems);

    const navigateToNextPage = () => {
      navigateTo(
        skipServingOptionsPage || pageContext.servingOptions.length < 2
          ? "/find-location"
          : "/serving-options"
      );
    };

    if (isDuringOrder) {
      dispatch(openNewOrPrevOrderModal({ context: 'featured', featuredProductId: item.id }));
    } else {
      dispatch(startNewOrder());
      dispatch(setFeaturedProductToByInOrder(item.id));
      navigateToNextPage();
    }
  };

  const settings = {
    infinite: false,
    speed: 1000,
    slidesToShow: 1.37, // to show a part of next slide
    slidesToScroll: 1,
    arrows: false,
    dots: false,
    focusOnSelect: true,
  };

  const renderCarusleMenuItem = (item, index) => (
    <div
      key={index}
      style={{
        height: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {get(item, "imageKey") ? (
        <Image
          mediaKey={item.imageKey}
          mediaType={mediaTypes.menuItem}
          imagePreview={item.imagePreview}
          isWidget
          eager
        />
      ) : (
        <NoImageRenderer appStyles={props.appStyles} isWidget />
      )}
    </div>
  );

  const allergensForItem = (item) =>
    filter(allergens, (allergen) => includes(item.allergens, allergen.id));

  const sliderContent = filter(featuredItems, (item) => item).map(
    (item, index) => (
      <div key={index}>
        <Paper
          elevation={3}
          sx={{
            margin: "5px",
            borderRadius: "10px",
          }}
        >
          {renderCarusleMenuItem(item)}
          <Box sx={{ p: "12px" }}>
            <Typography
              gutterBottom
              variant="h5"
              fontWeight="bold"
              component="div"
              sx={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "16px",
              }}
            >
              {item.name}
            </Typography>
            <Typography
              variant="body1"
              component={"span"}
              gutterBottom
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                height: "42px",
                lineHeight: "1.2em",
                color: appStyles.textColor,
                fontSize: "12px",
              }}
            >
              {item.description}
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: !isEmpty(allergensForItem(item))
                  ? "space-between"
                  : "flex-end",
                alignItems: "center",
              }}
            >
              <AllergensDecorator
                allergens={allergensForItem(item)}
                appStyles={appStyles}
                wrapperClassNames={classes.allergensWrapper}
              />
              <IconButton
                sx={{ padding: 0, width: 30, height: 30 }}
                onClick={() => handleAddFeaturedItemToOrder(item)}
              >
                <Avatar
                  sx={{
                    background: appStyles.actionColor,
                    width: 24,
                    height: 24,
                  }}
                >
                  <Add sx={{ width: 16, height: 16 }} />
                </Avatar>
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </div>
    )
  );

  return (
    featuredItems &&
    !isEmpty(featuredItems) && (
      <Card
        className={classes.featuredCard}
        style={{ borderRadius: appStyles.cardBorderRadius }}
      >
        <Box>
          {!isEmpty(widget.title) && (
            <Typography gutterBottom variant="h5" sx={{ fontWeight: "bolder", fontSize: "large" }}>
              {widget.title}
            </Typography>
          )}
          {!isEmpty(widget.subtitle) && (
            <Typography gutterBottom variant="subtitle1" component="h4">
              {widget.subtitle}
            </Typography>
          )}
          {!isEmpty(widget.content) && (
            <Typography variant="body1" component="p">
              {widget.content}
            </Typography>
          )}
          <Slider
            ref={sliderRef}
            {...settings}
            className={classes.featuredSlider}
          >
            {sliderContent}
          </Slider>
        </Box>
      </Card>
    )
  );
};

const desktopCardPromotionWidth = 376;

const useStyles = makeStyles()((theme) => ({
  featuredSlider: {
    height: "100%",
    maxHeight: `calc(100vw - ${theme.spacing(8)})`,
    width: `calc(100vw - ${theme.spacing(4)})`,
    [theme.breakpoints.up("sm")]: {
      height: "100%",
      maxHeight: "320px",
      width: `calc(${desktopCardPromotionWidth}px - ${theme.spacing(4)})`,
    },
  },
  featuredCard: {
    boxShadow: "none",
    borderRadius: 0,
    background: "transparent",
    [theme.breakpoints.up("sm")]: {
      boxShadow: theme.shadows[3],
      background: theme.palette.background.paper,
    },
  },
  allergensWrapper: {
    display: "inline-flex"
  },
}));

export default FeaturedWidget;
